import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { KeyRound, ShieldCheck, ShieldAlert, Trash2, RefreshCw } from 'lucide-react';
import { customers as api } from '../api';
import type { ProviderCredential } from '../api';
import type { Customer } from '../types';

/**
 * Die Anbieter-Zugänge eines Kunden.
 *
 * WOFÜR: „Legen Sie sich ein Konto bei Hetzner an und geben Sie uns einen
 * API-Schlüssel — Bestellung und alles danach übernehmen wir." Vertrag und
 * Rechnung bleiben beim Kunden, die Arbeit bei uns.
 *
 * **Optional.** Ohne Eintrag gilt der Haus-Zugang; ohne beides entfällt nur,
 * was die Anbieter-API braucht. Deshalb steht hier kein Warnzeichen, wenn
 * nichts hinterlegt ist — das ist der Normalfall und kein Mangel.
 *
 * Das Token verlässt den Server nie wieder. Es ist genau einmal sichtbar,
 * nämlich hier beim Eintippen.
 */

const ANBIETER_NAME: Record<string, string> = {
  hetzner: 'Hetzner Cloud',
  'hetzner-robot': 'Hetzner Robot (dedizierte Server)',
  hostinger: 'Hostinger',
};

/**
 * Robot meldet sich anders an als alles andere hier: HTTP Basic mit einem
 * Webservice-Benutzer statt eines Tokens. Gespeichert wird das als
 * `benutzer:passwort` — die Form, die Basic Auth ohnehin kodiert — aber
 * eingegeben in zwei Feldern, weil niemand einen Doppelpunkt tippen sollte,
 * dessen Bedeutung er nicht kennt.
 */
const BRAUCHT_PAAR = (provider: string) => provider === 'hetzner-robot';

function zeit(wert: string | null): string | null {
  if (!wert) return null;
  return new Date(wert).toLocaleDateString('de-DE', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
}

export default function AnbieterZugaengePanel({ customer }: { customer: Customer }) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [offen, setOffen] = useState<string | null>(null);
  const [token, setToken] = useState('');
  const [robotBenutzer, setRobotBenutzer] = useState('');
  const [robotPasswort, setRobotPasswort] = useState('');
  const [label, setLabel] = useState('');
  const [meldung, setMeldung] = useState('');
  const [fehler, setFehler] = useState('');

  const schluessel = ['customers', customer.id, 'provider-credentials'];

  const { data, isLoading } = useQuery({
    queryKey: schluessel,
    queryFn: () => api.providerCredentials(customer.id),
  });

  function beiFehler(vorgabe: string) {
    return (err: unknown) => {
      setFehler(
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message ?? vorgabe,
      );
      setMeldung('');
    };
  }

  const speichern = useMutation({
    mutationFn: (provider: string) =>
      api.setProviderCredential(
        customer.id,
        provider,
        BRAUCHT_PAAR(provider) ? `${robotBenutzer}:${robotPasswort}` : token,
        label || undefined,
      ),
    onSuccess: (r) => {
      qc.invalidateQueries({ queryKey: schluessel });
      setOffen(null);
      setToken('');
      setRobotBenutzer('');
      setRobotPasswort('');
      setLabel('');
      setFehler('');
      setMeldung(r.message);
    },
    onError: beiFehler('Speichern fehlgeschlagen.'),
  });

  const pruefen = useMutation({
    mutationFn: (provider: string) => api.checkProviderCredential(customer.id, provider),
    onSuccess: (r) => {
      qc.invalidateQueries({ queryKey: schluessel });
      setFehler('');
      setMeldung(
        r.ok ? t('providerCredentials.checkOk', { count: r.servers ?? 0 }) : (r.error ?? ''),
      );
    },
    onError: beiFehler('Prüfen fehlgeschlagen.'),
  });

  const entfernen = useMutation({
    mutationFn: (provider: string) => api.deleteProviderCredential(customer.id, provider),
    onSuccess: (r) => {
      qc.invalidateQueries({ queryKey: schluessel });
      setFehler('');
      setMeldung(r.message);
    },
    onError: beiFehler('Entfernen fehlgeschlagen.'),
  });

  const anbieter = data?.anbieter ?? ['hetzner', 'hostinger'];
  const vorhanden = new Map<string, ProviderCredential>(
    (data?.credentials ?? []).map((c) => [c.provider, c]),
  );

  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <h4 className="text-xs font-semibold text-slate-500 uppercase tracking-wide">
          {t('providerCredentials.title')}
        </h4>
      </div>

      <p className="text-xs text-slate-500 mb-3">{t('providerCredentials.explain')}</p>

      {fehler && <div className="text-xs text-red-600 mb-2">{fehler}</div>}
      {meldung && <div className="text-xs text-green-700 mb-2">{meldung}</div>}

      {isLoading ? (
        <div className="h-8 bg-slate-100 rounded animate-pulse" />
      ) : (
        <div className="space-y-2">
          {anbieter.map((provider) => {
            const eintrag = vorhanden.get(provider);
            const bearbeitet = offen === provider;

            return (
              <div
                key={provider}
                className="bg-surface border border-slate-200 rounded-lg p-3 text-xs"
              >
                <div className="flex items-center justify-between gap-2 flex-wrap">
                  <div className="flex items-center gap-2">
                    <KeyRound size={13} className="text-slate-400" />
                    <span className="font-medium text-slate-700">
                      {ANBIETER_NAME[provider] ?? provider}
                    </span>
                    {eintrag ? (
                      eintrag.lastCheckOk === false ? (
                        <span className="inline-flex items-center gap-1 text-amber-700">
                          <ShieldAlert size={12} /> {eintrag.lastCheckError}
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 text-green-700">
                          <ShieldCheck size={12} /> {eintrag.label || '—'}
                        </span>
                      )
                    ) : (
                      <span className="text-slate-400">{t('providerCredentials.noneTitle')}</span>
                    )}
                  </div>

                  <div className="flex items-center gap-2">
                    {eintrag && (
                      <>
                        <button
                          onClick={() => pruefen.mutate(provider)}
                          disabled={pruefen.isPending}
                          className="inline-flex items-center gap-1 text-brand-600 hover:text-brand-800 disabled:opacity-50"
                        >
                          <RefreshCw size={12} />
                          {pruefen.isPending
                            ? t('providerCredentials.checking')
                            : t('providerCredentials.check')}
                        </button>
                        <button
                          onClick={() => {
                            if (
                              window.confirm(
                                t('providerCredentials.removeConfirm', {
                                  provider: ANBIETER_NAME[provider] ?? provider,
                                }),
                              )
                            ) {
                              entfernen.mutate(provider);
                            }
                          }}
                          className="inline-flex items-center gap-1 text-red-600 hover:text-red-800"
                        >
                          <Trash2 size={12} /> {t('providerCredentials.remove')}
                        </button>
                      </>
                    )}
                    <button
                      onClick={() => {
                        setOffen(bearbeitet ? null : provider);
                        setToken('');
                        setRobotBenutzer('');
                        setRobotPasswort('');
                        setLabel(eintrag?.label ?? '');
                        setFehler('');
                        setMeldung('');
                      }}
                      className="text-brand-600 hover:text-brand-800 font-medium"
                    >
                      {eintrag
                        ? t('providerCredentials.replace')
                        : t('providerCredentials.add')}
                    </button>
                  </div>
                </div>

                {eintrag && !bearbeitet && (
                  <p className="text-slate-400 mt-1">
                    {eintrag.lastUsedAt
                      ? t('providerCredentials.lastUsed', { when: zeit(eintrag.lastUsedAt) })
                      : t('providerCredentials.neverUsed')}
                    {' · '}
                    {eintrag.lastCheckedAt
                      ? t('providerCredentials.lastChecked', { when: zeit(eintrag.lastCheckedAt) })
                      : t('providerCredentials.neverChecked')}
                  </p>
                )}

                {bearbeitet && (
                  <form
                    onSubmit={(e) => {
                      e.preventDefault();
                      setFehler('');
                      speichern.mutate(provider);
                    }}
                    className="mt-3 space-y-2 border-t border-slate-100 pt-3"
                  >
                    {/* Was der Schlüssel beim jeweiligen Anbieter überhaupt
                        umfasst. Das entscheidet, worum man den Kunden bittet —
                        und bei Hostinger, was man ihm dazusagen muss. */}
                    {/* Was der Zugang jeweils umfasst — und bei Hostinger und
                        Robot auch, was daran unangenehm ist. Beides gehört vor
                        das Eingabefeld und nicht in eine Fußnote. */}
                    <p
                      className={
                        provider === 'hetzner'
                          ? 'text-slate-500'
                          : 'text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1.5'
                      }
                    >
                      {provider === 'hostinger'
                        ? t('providerCredentials.scopeHostinger')
                        : provider === 'hetzner-robot'
                          ? t('providerCredentials.scopeRobot')
                          : t('providerCredentials.scopeHetzner')}
                    </p>

                    {BRAUCHT_PAAR(provider) ? (
                      <div className="grid grid-cols-2 gap-2">
                        <div>
                          <label className="block text-slate-500 mb-1">
                            {t('providerCredentials.robotUser')}
                          </label>
                          <input
                            required
                            autoComplete="off"
                            value={robotBenutzer}
                            onChange={(e) => setRobotBenutzer(e.target.value)}
                            className="w-full border border-slate-300 rounded px-2 py-1.5 font-mono focus:outline-none focus:ring-2 focus:ring-brand-500"
                          />
                        </div>
                        <div>
                          <label className="block text-slate-500 mb-1">
                            {t('providerCredentials.robotPassword')}
                          </label>
                          <input
                            required
                            autoComplete="off"
                            type="password"
                            value={robotPasswort}
                            onChange={(e) => setRobotPasswort(e.target.value)}
                            className="w-full border border-slate-300 rounded px-2 py-1.5 font-mono focus:outline-none focus:ring-2 focus:ring-brand-500"
                          />
                        </div>
                        <p className="col-span-2 text-slate-400">
                          {t('providerCredentials.tokenHint')}
                        </p>
                      </div>
                    ) : (
                      <div>
                        <label className="block text-slate-500 mb-1">
                          {t('providerCredentials.tokenLabel')}
                        </label>
                        <input
                          required
                          autoComplete="off"
                          // type=password, damit der Schlüssel nicht offen auf
                          // dem Schirm steht, wenn jemand danebensteht.
                          type="password"
                          value={token}
                          onChange={(e) => setToken(e.target.value)}
                          className="w-full border border-slate-300 rounded px-2 py-1.5 font-mono focus:outline-none focus:ring-2 focus:ring-brand-500"
                        />
                        <p className="text-slate-400 mt-1">{t('providerCredentials.tokenHint')}</p>
                      </div>
                    )}

                    <div>
                      <label className="block text-slate-500 mb-1">
                        {t('providerCredentials.labelField')}
                      </label>
                      <input
                        value={label}
                        onChange={(e) => setLabel(e.target.value)}
                        className="w-full border border-slate-300 rounded px-2 py-1.5 focus:outline-none focus:ring-2 focus:ring-brand-500"
                      />
                    </div>

                    <div className="flex gap-2">
                      <button
                        type="submit"
                        disabled={speichern.isPending}
                        className="bg-brand-600 hover:bg-brand-700 text-white font-medium px-3 py-1.5 rounded disabled:opacity-60"
                      >
                        {speichern.isPending
                          ? t('providerCredentials.saving')
                          : t('providerCredentials.save')}
                      </button>
                      <button
                        type="button"
                        onClick={() => setOffen(null)}
                        className="text-slate-500 hover:text-slate-700 px-2"
                      >
                        {t('providerCredentials.cancel')}
                      </button>
                    </div>
                  </form>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
